import { Calendar, Clock } from 'lucide-react'
import type { PetDashboardSummary, UrgencyStatus } from '@rondela/shared'
import type { SpeciesType } from '../../../shared/components/widgets/StatusCard'
import { StatusCard, Avatar, Badge } from '../../../shared/components/widgets'
import { parseLocalDateTime } from '../../../shared/lib/date-utils'

interface PetListCardProps {
  summary: PetDashboardSummary
  isSelected: boolean
  onSelect: () => void
}

function getSpeciesEmoji(especie: string): string {
  return especie === 'perro' ? '🐕' : especie === 'gato' ? '🐈' : '🐾'
}

function getSpeciesType(especie: string): SpeciesType {
  if (especie === 'perro') return 'perro'
  if (especie === 'gato') return 'gato'
  return 'otro'
}

function getStatusType(urgencyStatus: UrgencyStatus) {
  switch (urgencyStatus) {
    case 'overdue':
      return 'error' as const
    case 'urgent':
      return 'warning' as const
    default:
      return 'neutral' as const
  }
}

function formatDate(dateStr: string): string {
  const date = parseLocalDateTime(dateStr)
  return date.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatTime(dateStr: string): string {
  const date = parseLocalDateTime(dateStr)
  return date.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })
}

export function PetListCard({ summary, isSelected, onSelect }: PetListCardProps) {
  const statusType = getStatusType(summary.urgencyStatus)

  return (
    <StatusCard
      status={statusType}
      stripe
      stripeIcon={getSpeciesType(summary.especie)}
      selectable
      selected={isSelected}
      onSelect={onSelect}
      className="pet-list-card"
    >
      <StatusCard.Header>
        <Avatar
          src={summary.fotoUrl}
          fallback={getSpeciesEmoji(summary.especie)}
          size="md"
          ring={statusType !== 'neutral'}
          ringColor={statusType}
        />
        <div className="pet-list-card__info">
          <div className="pet-list-card__name-row">
            <StatusCard.Title>{summary.nombre}</StatusCard.Title>
            {summary.peso && <Badge variant="pill">{summary.peso} kg</Badge>}
          </div>

          {/* Date badge from next appointment */}
          {summary.nextAppointment && (
            <div className="pet-list-card__appointment">
              <Badge variant="pill" status="error" className="pet-list-card__date-badge">
                <Calendar size={12} />
                {formatDate(summary.nextAppointment.date)}
              </Badge>
              <span className="pet-list-card__description">{summary.nextAppointment.type}</span>
            </div>
          )}

          {/* Next dose time badge */}
          {summary.nextDose && (
            <Badge variant="pill" status="warning" className="pet-list-card__time-badge">
              <Clock size={12} />
              {formatTime(summary.nextDose.scheduledFor)}
            </Badge>
          )}
        </div>

        <div className="pet-list-card__meta">
          {summary.pendingDosesCount > 0 && (
            <Badge variant="counter" status="warning">
              {summary.pendingDosesCount}
            </Badge>
          )}
        </div>
      </StatusCard.Header>
    </StatusCard>
  )
}
